import { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2, Search } from 'lucide-react';

/**
 * Address autocomplete using OpenStreetMap Nominatim (free, no API key).
 * On selection, calls onSelect({ address, lat, lng }).
 */
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder = 'Type a street address…' }) {
  const [q, setQ] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setQ(value || ''); }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const search = async (text) => {
    if (!text || text.trim().length < 3) { setSuggestions([]); return; }
    setBusy(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&countrycodes=us&q=${encodeURIComponent(text)}`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const data = await res.json();
      setSuggestions(data || []);
    } catch { setSuggestions([]); }
    finally { setBusy(false); }
  };

  const onType = (v) => {
    setQ(v);
    onChange?.(v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 350);
  };

  const pick = (s) => {
    const a = s.address || {};
    const streetLine = [a.house_number, a.road].filter(Boolean).join(' ');
    const city = a.city || a.town || a.village || a.hamlet || a.municipality || '';
    const stateAbbrev = a['ISO3166-2-lvl4']?.split('-')?.[1] || a.state || '';
    const zip = a.postcode || '';
    const formatted = [streetLine, [city, stateAbbrev].filter(Boolean).join(', '), zip].filter(Boolean).join(', ');
    const lat = parseFloat(s.lat), lng = parseFloat(s.lon);
    setQ(formatted || s.display_name);
    setOpen(false);
    onSelect?.({ address: formatted || s.display_name, lat, lng });
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-muted" />
        <input
          data-testid="address-autocomplete-input"
          type="text"
          value={q}
          onFocus={() => q.length >= 3 && setOpen(true)}
          onChange={(e) => onType(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-10 py-2 border rounded"
        />
        {busy && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-royal" />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={`${s.place_id || i}`}
              type="button"
              data-testid={`address-suggestion-${i}`}
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2 hover:bg-royal/5 border-b last:border-0 flex items-start gap-2"
            >
              <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <div className="text-sm text-charcoal">{s.display_name}</div>
            </button>
          ))}
        </div>
      )}
      {open && !busy && q.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border rounded-lg shadow-xl px-3 py-2 text-xs text-charcoal-muted">
          No matches yet — keep typing.
        </div>
      )}
    </div>
  );
}
